import React from "react";
import Alert, { type AlertVariant } from "../ui/Alert/Alert";

interface FlashMessageItem {
  type?: AlertVariant | string;
  content?: unknown;
  message?: unknown;
}

type FlashMessageInput =
  | React.ReactNode
  | FlashMessageItem
  | FlashMessageItem[]
  | string
  | number
  | null
  | undefined;

interface FlashMessageProps {
  message?: FlashMessageInput;
  className?: string;
  role?: string;
  ariaLive?: string;
}

function flattenMessageContent(content: unknown, prefix = ""): Array<string | React.ReactElement> {
  if (!content) {
    return [];
  }

  if (React.isValidElement(content)) {
    return [content];
  }

  if (Array.isArray(content)) {
    return content.flatMap((item) => flattenMessageContent(item, prefix));
  }

  if (typeof content === "string" || typeof content === "number") {
    return [prefix ? `${prefix} ${content}` : String(content)];
  }

  if (typeof content === "object") {
    return Object.entries(content as Record<string, unknown>).flatMap(([key, value]) =>
      flattenMessageContent(value, key ? `${key}:` : prefix)
    );
  }

  return [];
}

type NormalizedItem = React.ReactElement | { type: string; content: React.ReactNode };

function normalizeItems(message: FlashMessageInput): NormalizedItem[] {
  if (!message) {
    return [];
  }

  if (React.isValidElement(message)) {
    return [message];
  }

  if (Array.isArray(message)) {
    return message.filter(Boolean).map((item) => {
      if (React.isValidElement(item)) {
        return item;
      }

      if (typeof item === "string" || typeof item === "number") {
        return { type: "info", content: String(item) };
      }

      return item as NormalizedItem;
    });
  }

  if (typeof message === "string" || typeof message === "number") {
    return [{ type: "info", content: String(message) }];
  }

  if (typeof message === "object") {
    const m = message as FlashMessageItem;
    const rawContent = typeof m.content !== "undefined" ? m.content : m.message;
    const messages = flattenMessageContent(rawContent);

    return messages.map((content) => ({
      type: m.type || "info",
      content,
    }));
  }

  return [];
}

const FlashMessage: React.FC<FlashMessageProps> = ({ message, className }) => {
  const items = normalizeItems(message);

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={`FlashMessageGroup${className ? ` ${className}` : ""}`}>
      {items.map((item, index) => {
        if (React.isValidElement(item)) {
          return <React.Fragment key={index}>{item}</React.Fragment>;
        }

        const plain = item as { type: string; content: React.ReactNode };
        const itemType = plain.type || "info";
        return (
          <Alert
            key={index}
            variant={itemType as AlertVariant}
            className={`FlashMessage FlashMessage--${itemType}`}
          >
            <span className="FlashMessage-content">{plain.content}</span>
          </Alert>
        );
      })}
    </div>
  );
};

export default FlashMessage;
